import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, errText, mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Avatar, Loading, Empty, formatNum } from "../components/common";
import { CrewEmblem } from "./CrewsDiscover";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Users, Star, Shield, Share2, UserPlus, LogOut, Send, Pin, Hash,
  MessageSquare, Image as ImageIcon, Handshake, Calendar, Trophy, Info, Heart,
  MapPin, Plus, X, Copy, AlertTriangle, Settings as SettingsIcon, Flag, Award, Film, Link2, Instagram, Loader2, Lock, Search
} from "lucide-react";
import { ReportImpersonationModal } from "../components/ReportImpersonationModal";
import { StoryShareModal } from "../components/StoryShareModal";
import { useUpload } from "../lib/useUpload";
import { crewUrl, ogCrewUrl } from "../lib/config";
import { crewAccessLabel, crewAccessSummary, joinButtonLabel } from "../lib/crewAccess";
import { toast } from "sonner";

const ROLE_BADGE = { founder: "Founder", co_owner: "Co-Owner", manager: "Manager", officer: "Officer", member: "Member" };
const ROLE_LEVEL = { founder: 4, co_owner: 3, manager: 2, officer: 1, member: 0 };

export default function CrewHQ() {
  const { handle } = useParams();
  const h = (handle || "").replace(/^@/, "");
  const { user } = useAuth();
  const nav = useNavigate();
  const [crew, setCrew] = useState(null);
  const [tab, setTab] = useState("home");
  const [shareOpen, setShareOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const reload = useCallback(() => api.get(`/crews/handle/${h}`).then(({ data }) => setCrew(data)).catch(() => setCrew(false)), [h]);
  useEffect(() => { setCrew(null); reload(); }, [h]); // eslint-disable-line

  if (crew === null) return <Loading />;
  if (crew === false) return <Empty icon={Users} title="Crew not found" action={<Button onClick={() => nav("/crews")} className="btn-gold rounded-full">Explore Crews</Button>} />;

  const m = crew.my_membership;
  const isMember = m?.status === "active";
  const myLevel = isMember ? ROLE_LEVEL[m.role] : -1;
  const isManager = myLevel >= 2 || user?.role === "admin";
  const isFounder = m?.role === "founder" || user?.role === "admin";
  const contentLocked = crew.content_locked && !isMember && user?.role !== "admin";

  const join = async () => {
    try { const { data } = await api.post(`/crews/${crew.id}/join`); toast.success(data.status === "requested" ? "Request sent!" : "Welcome to the crew!"); reload(); }
    catch (e) { toast.error(errText(e)); }
  };
  const leave = async () => { try { await api.post(`/crews/${crew.id}/leave`); toast.success("Left crew"); reload(); } catch (e) { toast.error(errText(e)); } };
  const setPrimary = async () => { try { await api.post(`/crews/${crew.id}/set-primary`); toast.success("Set as primary crew"); reload(); } catch (e) { toast.error(errText(e)); } };

  const tabs = [["home", Shield], ["members", Users], ["feed", MessageSquare], ...(isMember ? [["chat", Hash]] : []), ["media", ImageIcon], ["collabs", Handshake], ["events", Calendar], ["rankings", Trophy], ["about", Info]];

  return (
    <div className="max-w-5xl mx-auto pb-10">
      <div className="relative h-40 md:h-56 bg-gradient-to-br from-[#131826] to-black">
        {crew.banner_url && <img src={mediaUrl(crew.banner_url)} className="w-full h-full object-cover" alt="" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#030303] to-transparent" />
      </div>
      <div className="px-4 md:px-6 -mt-12 relative">
        {crew.management_restricted && (
          <div className="mb-3 surface rounded-xl p-3 text-xs flex items-center gap-2 text-amber-400 border border-amber-500/20" data-testid="crew-restricted-banner">
            <AlertTriangle size={14} /> Crew management is limited — the founder's VirtuStars+ has lapsed. Content and members are safe; renew Plus or transfer ownership to restore full management.
          </div>
        )}
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div className="ring-4 ring-[#030303] rounded-full"><CrewEmblem crew={crew} size={96} /></div>
          <div className="flex gap-2 pb-1 flex-wrap">
            <Button onClick={() => setShareOpen(true)} variant="outline" className="rounded-full border-white/15 bg-white/5" data-testid="crew-share-btn"><Share2 size={15} /></Button>
            <Button onClick={() => { navigator.clipboard?.writeText(ogCrewUrl(crew.id)); toast.success("Crew link copied — rich preview ready"); }} variant="outline" className="rounded-full border-white/15 bg-white/5" title="Copy shareable link" data-testid="crew-copylink-btn"><Link2 size={15} /></Button>
            <Button onClick={() => user ? setReportOpen(true) : nav("/register")} variant="outline" className="rounded-full border-white/15 bg-white/5 text-muted-foreground" title="Report impersonation" data-testid="crew-report-btn"><Flag size={15} /></Button>
            {isMember && isManager && <Button onClick={() => setSettingsOpen(true)} variant="outline" className="rounded-full border-white/15 bg-white/5" data-testid="crew-edit-btn"><SettingsIcon size={15} className="mr-1" />Edit Crew</Button>}
            {isMember && isManager && <Button onClick={() => setTab("members")} variant="outline" className="rounded-full border-white/15 bg-white/5" data-testid="crew-manage-btn"><Users size={15} className="mr-1" />Manage</Button>}
            {!isMember && (m?.status === "requested"
              ? <Button disabled variant="outline" className="rounded-full border-white/15 bg-white/5" data-testid="crew-requested-btn">Request pending</Button>
              : <Button onClick={user ? join : () => nav("/register")} className="btn-gold rounded-full" data-testid="crew-join-btn"><UserPlus size={15} className="mr-1" />{joinButtonLabel(crew.join_policy)}</Button>)}
            {isMember && !isFounder && <Button onClick={leave} variant="outline" className="rounded-full border-white/15 bg-white/5 text-rose-400" data-testid="crew-leave-btn"><LogOut size={15} className="mr-1" />Leave</Button>}
            {isMember && !m.is_primary && <Button onClick={setPrimary} variant="outline" className="rounded-full border-white/15 bg-white/5" data-testid="crew-setprimary-btn"><Star size={15} className="mr-1" />Set Primary</Button>}
          </div>
        </div>
        <div className="mt-3">
          <h1 className="font-display font-extrabold text-2xl md:text-3xl flex items-center gap-2">{crew.name}{crew.member_count >= 10 && <Star size={18} className="text-gold" />}</h1>
          <div className="text-muted-foreground text-sm">@{crew.handle} · Founded by <span className="text-white cursor-pointer hover:text-eblue" onClick={() => nav(`/${crew.founder?.username}`)}>{crew.founder?.display_name}</span></div>
          {crew.description && <p className="text-sm mt-2 max-w-2xl">{crew.description}</p>}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <span className="text-xs surface-2 rounded-full px-2.5 py-1 flex items-center gap-1"><MapPin size={11} />{crew.primary_world}</span>
            {(crew.categories || []).map((c) => <span key={c} className="text-xs rounded-full px-2.5 py-1 bg-gold/10 text-gold border border-gold/20">{c}</span>)}
            {isMember && <span className="text-xs rounded-full px-2.5 py-1 bg-eblue/10 text-eblue border border-eblue/30">{ROLE_BADGE[m.role]}</span>}
          </div>
        </div>
        <div className="grid grid-cols-3 md:grid-cols-4 gap-3 mt-5">
          <Stat label="Members" value={`${crew.member_count}/${crew.member_limit}`} />
          <Stat label="Crew Score" value={<span className="text-gold">{formatNum(crew.score)}</span>} />
          <Stat label="Achievements" value={crew.achievements?.length || 0} />
          <Stat label="Privacy" value={<span className="text-sm">{crewAccessLabel(crew.content_visibility, crew.join_policy)}</span>} />
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mt-6">
          <TabsList className="bg-transparent border-b border-white/8 w-full justify-start rounded-none h-auto p-0 gap-1 overflow-x-auto">
            {tabs.map(([t, Icon]) => <TabsTrigger key={t} value={t} data-testid={`crew-tab-${t}`} className="capitalize rounded-none border-b-2 border-transparent data-[state=active]:border-gold data-[state=active]:text-gold data-[state=active]:bg-transparent px-3 pb-3 gap-1.5"><Icon size={14} />{t}</TabsTrigger>)}
          </TabsList>

          <TabsContent value="home" className="pt-5">{contentLocked ? <PrivateGate crew={crew} /> : <HomeTab crew={crew} />}</TabsContent>
          <TabsContent value="members" className="pt-5"><MembersTab crew={crew} isManager={isManager} isFounder={isFounder} reload={reload} /></TabsContent>
          <TabsContent value="feed" className="pt-5">{contentLocked ? <PrivateGate crew={crew} /> : <FeedTab crew={crew} isMember={isMember} />}</TabsContent>
          {isMember && <TabsContent value="chat" className="pt-5"><ChatTab crew={crew} isManager={isManager} /></TabsContent>}
          <TabsContent value="media" className="pt-5">{contentLocked ? <PrivateGate crew={crew} /> : <MediaTab crew={crew} />}</TabsContent>
          <TabsContent value="collabs" className="pt-5">{contentLocked ? <PrivateGate crew={crew} /> : <CollabsTab crew={crew} isMember={isMember} />}</TabsContent>
          <TabsContent value="events" className="pt-5">{contentLocked ? <PrivateGate crew={crew} /> : <EventsTab crew={crew} isMember={isMember} canCreate={myLevel >= 1 || user?.role === "admin"} />}</TabsContent>
          <TabsContent value="rankings" className="pt-5"><RankingsTab crew={crew} /></TabsContent>
          <TabsContent value="about" className="pt-5"><AboutTab crew={crew} /></TabsContent>
        </Tabs>
      </div>
      {shareOpen && <ShareCrewModal crew={crew} onClose={() => setShareOpen(false)} />}
      {settingsOpen && <CrewSettingsModal crew={crew} onClose={() => setSettingsOpen(false)} onSaved={(c) => { setCrew(c); setSettingsOpen(false); }} />}
      <ReportImpersonationModal open={reportOpen} onClose={() => setReportOpen(false)} targetType="crew" targetId={crew.id} targetLabel={`@${crew.handle}`} />
    </div>
  );
}

function CrewSettingsModal({ crew, onClose, onSaved }) {
  const emblemRef = useRef(); const bannerRef = useRef();
  const [f, setF] = useState({
    name: crew.name || "", handle: crew.handle, description: crew.description || "",
    abbreviation: crew.abbreviation || "", primary_world: crew.primary_world || "Second Life",
    categories: crew.categories || [],
    content_visibility: crew.content_visibility || (crew.privacy === "private" ? "members" : "public"),
    join_policy: crew.join_policy || ({ request: "request", invite: "invite" }[crew.privacy] || "open"),
    member_limit: crew.member_limit || "", links: crew.links || [],
    emblem_url: crew.emblem_url || null, banner_url: crew.banner_url || null,
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(null);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const upload = async (file, key) => {
    setUploading(key);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); set(key, data.url); }
    catch (e) { toast.error(errText(e)); } finally { setUploading(null); }
  };

  const save = async () => {
    if (!f.name.trim()) return toast.error("Crew name is required");
    setBusy(true);
    try { const { data } = await api.put(`/crews/${crew.id}`, f); toast.success("Crew updated"); onSaved(data); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="surface rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="crew-settings-modal">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h3 className="font-display font-semibold text-lg">Edit Crew</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <div className="text-xs text-muted-foreground mb-1.5">Cover art</div>
            <div className="relative h-28 rounded-xl bg-gradient-to-br from-[#131826] to-black overflow-hidden cursor-pointer border border-white/10" onClick={() => bannerRef.current?.click()} data-testid="crew-settings-banner">
              {f.banner_url ? <img src={mediaUrl(f.banner_url)} className="w-full h-full object-cover" alt="" /> : <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground gap-1"><ImageIcon size={15} />{uploading === "banner_url" ? "Uploading…" : "Add cover art"}</div>}
              <input ref={bannerRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && upload(e.target.files[0], "banner_url")} />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div>
              <div className="text-xs text-muted-foreground mb-1.5">Logo</div>
              <div className="w-20 h-20 rounded-full surface-2 border border-white/10 overflow-hidden cursor-pointer flex items-center justify-center shrink-0" onClick={() => emblemRef.current?.click()} data-testid="crew-settings-emblem">
                {f.emblem_url ? <img src={mediaUrl(f.emblem_url)} className="w-full h-full object-cover" alt="" /> : <Plus size={18} className="text-muted-foreground" />}
                <input ref={emblemRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && upload(e.target.files[0], "emblem_url")} />
              </div>
            </div>
            <p className="text-xs text-muted-foreground flex-1">Upload a square logo and a wide cover image that represent your crew. Tap each to change.</p>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1.5">Crew name</div>
            <Input value={f.name} onChange={(e) => set("name", e.target.value)} className="surface-2 border-white/10" data-testid="crew-settings-name" />
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1.5">Description</div>
            <Textarea value={f.description} onChange={(e) => set("description", e.target.value)} rows={3} className="surface-2 border-white/10" data-testid="crew-settings-desc" />
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1.5">Content visibility</div>
            <select value={f.content_visibility} onChange={(e) => set("content_visibility", e.target.value)} className="w-full surface-2 border border-white/10 rounded-md h-10 px-2 text-sm" data-testid="crew-settings-visibility">
              <option value="public">Public — anyone can view posts & media</option>
              <option value="members">Members only — content hidden until you join</option>
            </select>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1.5">Who can join</div>
            <select value={f.join_policy} onChange={(e) => set("join_policy", e.target.value)} className="w-full surface-2 border border-white/10 rounded-md h-10 px-2 text-sm" data-testid="crew-settings-join">
              <option value="open">Open — anyone can join</option>
              <option value="request">Request — approve each member</option>
              <option value="invite">Invite only — owners send invites</option>
            </select>
          </div>
          <div className="text-[11px] text-muted-foreground surface-2 rounded-lg p-2.5" data-testid="crew-settings-access-summary">{crewAccessSummary(f.content_visibility, f.join_policy)}</div>
          <Button onClick={save} disabled={busy || uploading} className="btn-gold w-full rounded-xl h-11" data-testid="crew-settings-save">{busy ? "Saving…" : "Save changes"}</Button>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value }) => <div className="surface rounded-xl p-3 text-center"><div className="font-display font-bold text-lg">{value}</div><div className="text-xs text-muted-foreground">{label}</div></div>;

function PrivateGate({ crew }) {
  return (
    <div className="surface rounded-2xl p-10 text-center max-w-md mx-auto" data-testid="crew-private-gate">
      <div className="w-14 h-14 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center mx-auto mb-3 text-gold"><Lock size={24} /></div>
      <div className="font-display font-bold text-lg">This crew is private</div>
      <p className="text-sm text-muted-foreground mt-1.5">
        {crew.join_policy === "invite"
          ? "You need an invite from an owner or manager to view this crew's content."
          : "Join this crew to see its posts, media, collabs and events."}
      </p>
    </div>
  );
}

function HomeTab({ crew }) {
  return (
    <div className="grid md:grid-cols-3 gap-4">
      <div className="md:col-span-2 space-y-4">
        <FeedTab crew={crew} isMember={crew.my_membership?.status === "active"} compact />
      </div>
      <div className="space-y-4">
        <div className="surface rounded-2xl p-4">
          <div className="text-sm font-semibold mb-2 flex items-center gap-2"><Trophy size={15} className="text-gold" />Achievements</div>
          {crew.achievements?.length ? <div className="space-y-1.5">{crew.achievements.map((a) => <div key={a.key} className="flex items-center gap-2 text-xs"><Star size={12} className="text-gold" />{a.label}</div>)}</div> : <p className="text-xs text-muted-foreground">No achievements yet — grow the crew to unlock trophies.</p>}
        </div>
        <div className="surface rounded-2xl p-4">
          <div className="text-sm font-semibold mb-2">Links</div>
          {crew.links?.length ? crew.links.map((l, i) => <a key={i} href={l.url} target="_blank" rel="noreferrer" className="block text-xs text-eblue hover:underline">{l.label || l.url}</a>) : <p className="text-xs text-muted-foreground">No links added.</p>}
        </div>
      </div>
    </div>
  );
}

function InviteBox({ crew }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [invited, setInvited] = useState({});
  useEffect(() => {
    const term = q.trim();
    if (!term) { setResults([]); return; }
    const t = setTimeout(() => api.get(`/mentions/search?q=${encodeURIComponent(term)}`).then(({ data }) => setResults(data)).catch(() => setResults([])), 300);
    return () => clearTimeout(t);
  }, [q]);
  const invite = async (u) => {
    try { await api.post(`/crews/${crew.id}/invite`, { user_id: u.id }); setInvited((x) => ({ ...x, [u.id]: true })); toast.success(`Invited ${u.display_name}`); }
    catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="surface rounded-2xl p-4" data-testid="crew-invite-box">
      <div className="text-sm font-semibold mb-2 flex items-center gap-2"><UserPlus size={15} className="text-eblue" />Invite members</div>
      <div className="flex items-center gap-2 surface-2 border border-white/10 rounded-full px-3 h-10">
        <Search size={15} className="text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search creators by @username or name" className="bg-transparent flex-1 outline-none text-sm" data-testid="crew-invite-search" />
      </div>
      {results.length > 0 && (
        <div className="mt-2 space-y-1.5">
          {results.map((u) => (
            <div key={u.id} className="flex items-center gap-3 surface-2 rounded-xl p-2">
              <Avatar src={u.avatar_url} name={u.display_name} size={32} />
              <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{u.display_name}</div><div className="text-xs text-muted-foreground truncate">@{u.username}</div></div>
              <Button onClick={() => invite(u)} disabled={invited[u.id]} size="sm" className="btn-blue rounded-lg" data-testid={`crew-invite-${u.username}`}>{invited[u.id] ? "Invited" : "Invite"}</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MembersTab({ crew, isManager, isFounder, reload }) {
  const [members, setMembers] = useState(null);
  const [requests, setRequests] = useState([]);
  const load = () => api.get(`/crews/${crew.id}/members`).then(({ data }) => setMembers(data));
  useEffect(() => { load(); if (isManager) api.get(`/crews/${crew.id}/requests`).then(({ data }) => setRequests(data)).catch(() => {}); }, [crew.id]); // eslint-disable-line
  const act = async (uid, action) => { try { await api.post(`/crews/${crew.id}/requests/${uid}/action?action=${action}`); setRequests((r) => r.filter((x) => x.user_id !== uid)); if (action === "approve") load(); toast.success(action === "approve" ? "Approved" : "Denied"); } catch (e) { toast.error(errText(e)); } };
  const setRole = async (uid, role) => { try { await api.post(`/crews/${crew.id}/members/${uid}/role`, { role }); load(); toast.success("Role updated"); } catch (e) { toast.error(errText(e)); } };
  const remove = async (uid) => { try { await api.post(`/crews/${crew.id}/members/${uid}/remove`); load(); toast.success("Removed"); } catch (e) { toast.error(errText(e)); } };

  return (
    <div className="space-y-4 max-w-2xl">
      {isManager && <InviteBox crew={crew} />}
      {isManager && requests.length > 0 && (
        <div className="surface rounded-2xl p-4" data-testid="crew-requests">
          <div className="text-sm font-semibold mb-2">Join Requests ({requests.length})</div>
          <div className="space-y-2">{requests.map((r) => (
            <div key={r.user_id} className="flex items-center gap-3 surface-2 rounded-xl p-2.5">
              <Avatar src={r.user?.avatar_url} name={r.user?.display_name} size={34} />
              <div className="flex-1 min-w-0 text-sm font-medium truncate">{r.user?.display_name}</div>
              <Button onClick={() => act(r.user_id, "approve")} size="sm" className="btn-blue rounded-lg" data-testid={`req-approve-${r.user_id}`}>Approve</Button>
              <Button onClick={() => act(r.user_id, "deny")} size="sm" variant="outline" className="rounded-lg border-white/15 bg-white/5">Deny</Button>
            </div>
          ))}</div>
        </div>
      )}
      {!members ? <Loading /> : (
        <div className="space-y-2" data-testid="crew-members-list">
          {members.map((mem) => (
            <div key={mem.user_id} className="flex items-center gap-3 surface rounded-xl p-3">
              <Avatar src={mem.user?.avatar_url} name={mem.user?.display_name} size={40} />
              <div className="flex-1 min-w-0">
                <a href={`/${mem.user?.username}`} className="text-sm font-medium hover:text-eblue">{mem.user?.display_name}</a>
                <div className="text-xs text-muted-foreground">@{mem.user?.username} · {ROLE_BADGE[mem.role]}{mem.is_primary ? " · ⭐ primary" : ""}</div>
              </div>
              {isManager && mem.role !== "founder" && (
                <div className="flex items-center gap-1.5">
                  <select value={mem.role} onChange={(e) => setRole(mem.user_id, e.target.value)} className="surface-2 border border-white/10 rounded-md text-xs h-8 px-1" data-testid={`member-role-${mem.user_id}`}>
                    {isFounder && <option value="co_owner">Co-Owner</option>}
                    <option value="manager">Manager</option><option value="officer">Officer</option><option value="member">Member</option>
                  </select>
                  <button onClick={() => remove(mem.user_id)} className="text-rose-400 p-1" data-testid={`member-remove-${mem.user_id}`}><X size={15} /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FeedTab({ crew, isMember, compact }) {
  const [posts, setPosts] = useState(null);
  const [text, setText] = useState("");
  const [media, setMedia] = useState(null);
  const [mediaType, setMediaType] = useState("image");
  const [link, setLink] = useState("");
  const [showLink, setShowLink] = useState(false);
  const { upload, uploading } = useUpload();
  const fileRef = useRef();
  const load = () => api.get(`/crews/${crew.id}/posts`).then(({ data }) => setPosts(data));
  useEffect(() => { load(); }, [crew.id]); // eslint-disable-line
  const pickFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
    try { const url = await upload(f); setMedia(url); setMediaType(f.type.startsWith("video") ? "video" : "image"); }
    catch (err) { toast.error(errText(err)); }
  };
  const post = async () => {
    if (!text.trim() && !media && !link.trim()) return;
    try {
      await api.post(`/crews/${crew.id}/posts`, { text, media_url: media, media_type: media ? mediaType : undefined, link: link.trim() || undefined });
      setText(""); setMedia(null); setLink(""); setShowLink(false); load();
    } catch (e) { toast.error(errText(e)); }
  };
  const like = async (id) => { try { const { data } = await api.post(`/crews/posts/${id}/like`); setPosts((ps) => ps.map((p) => p.id === id ? { ...p, liked: data.liked, like_count: p.like_count + (data.liked ? 1 : -1) } : p)); } catch {} };
  return (
    <div className="max-w-2xl space-y-4">
      {isMember && (
        <div className="surface rounded-2xl p-3">
          <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Share with your crew…" className="surface-2 border-white/10 min-h-[60px]" data-testid="crew-post-input" />
          {media && (
            <div className="relative mt-2 inline-block">
              {mediaType === "video" ? <video src={mediaUrl(media)} className="max-h-48 rounded-lg" controls /> : <img src={mediaUrl(media)} className="max-h-48 rounded-lg" alt="" />}
              <button onClick={() => setMedia(null)} className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-black/80 border border-white/20 flex items-center justify-center" data-testid="crew-post-media-remove"><X size={13} /></button>
            </div>
          )}
          {showLink && <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://link.com" className="w-full surface-2 border border-white/10 rounded-lg px-3 h-9 text-sm mt-2 outline-none" data-testid="crew-post-link-input" />}
          <input ref={fileRef} type="file" accept="image/*,image/gif,video/*" className="hidden" onChange={pickFile} data-testid="crew-post-file" />
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-1 text-muted-foreground">
              <button onClick={() => fileRef.current?.click()} disabled={uploading} className="p-2 rounded-lg hover:text-eblue" title="Photo / GIF / Video" data-testid="crew-post-attach-btn">{uploading ? <Loader2 size={18} className="animate-spin text-eblue" /> : <ImageIcon size={18} />}</button>
              <button onClick={() => setShowLink((s) => !s)} className={`p-2 rounded-lg hover:text-eblue ${showLink ? "text-eblue" : ""}`} title="Add link" data-testid="crew-post-link-btn"><Link2 size={18} /></button>
            </div>
            <Button onClick={post} className="btn-gold rounded-full" data-testid="crew-post-submit">Post</Button>
          </div>
        </div>
      )}
      {!posts ? <Loading /> : posts.length === 0 ? <Empty title="No posts yet" /> : posts.slice(0, compact ? 5 : 50).map((p) => (
        <div key={p.id} className="surface rounded-2xl p-4" data-testid={`crew-post-${p.id}`}>
          <div className="flex items-center gap-2.5 mb-2"><Avatar src={p.author?.avatar_url} name={p.author?.display_name} size={34} /><div><div className="text-sm font-medium">{p.author?.display_name}</div><div className="text-xs text-muted-foreground">{new Date(p.created_at).toLocaleString()}</div></div></div>
          {p.text && <p className="text-sm whitespace-pre-wrap">{p.text}</p>}
          {p.media_url && (p.media_type === "video"
            ? <video src={mediaUrl(p.media_url)} className="rounded-xl mt-2 max-h-96 w-full" controls />
            : <img src={mediaUrl(p.media_url)} className="rounded-xl mt-2 max-h-96 w-full object-cover" alt="" />)}
          {p.link && <a href={p.link} target="_blank" rel="noreferrer" className="mt-2 flex items-center gap-2 surface-2 rounded-lg px-3 py-2 text-xs text-eblue hover:underline truncate"><Link2 size={14} className="shrink-0" />{p.link}</a>}
          <button onClick={() => like(p.id)} className={`flex items-center gap-1 text-xs mt-3 ${p.liked ? "text-rose-400" : "text-muted-foreground"}`} data-testid={`crew-post-like-${p.id}`}><Heart size={14} fill={p.liked ? "currentColor" : "none"} />{p.like_count}</button>
        </div>
      ))}
    </div>
  );
}

function ChatTab({ crew, isManager }) {
  const [channels, setChannels] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [newCh, setNewCh] = useState("");
  const [attach, setAttach] = useState(null);
  const [attachType, setAttachType] = useState("image");
  const { upload, uploading } = useUpload();
  const chatFileRef = useRef();
  const wsRef = useRef(); const endRef = useRef();

  useEffect(() => { api.get(`/crews/${crew.id}/channels`).then(({ data }) => { setChannels(data); setActive(data[0]); }); }, [crew.id]);
  useEffect(() => { if (active) api.get(`/crews/${crew.id}/channels/${active.id}/messages`).then(({ data }) => setMessages(data)); }, [active]); // eslint-disable-line

  useEffect(() => {
    const token = localStorage.getItem("vs_token");
    if (!token) return;
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
    const ws = new WebSocket(`${base}/api/ws/crew/${crew.id}?token=${token}`);
    wsRef.current = ws;
    ws.onmessage = (e) => {
      try { const msg = JSON.parse(e.data); if (msg.event === "crew_message") setMessages((m) => active && msg.data.channel_id === active.id ? [...m, msg.data] : m); if (msg.event === "crew_pin") setMessages((m) => m.map((x) => x.id === msg.data.message_id ? { ...x, pinned: msg.data.pinned } : x)); } catch {}
    };
    return () => ws.close();
  }, [crew.id, active]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if ((!text.trim() && !attach) || !active) return;
    const t = text; const a = attach; const at = attachType;
    setText(""); setAttach(null);
    try { await api.post(`/crews/${crew.id}/channels/${active.id}/messages`, { text: t, attachment_url: a, attachment_type: a ? at : undefined }); }
    catch (e) { toast.error(errText(e)); setText(t); setAttach(a); }
  };
  const pickChatFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
    try { const url = await upload(f); setAttach(url); setAttachType(f.type.startsWith("video") ? "video" : "image"); }
    catch (err) { toast.error(errText(err)); }
  };
  const createCh = async () => { if (!newCh.trim()) return; try { const { data } = await api.post(`/crews/${crew.id}/channels`, { name: newCh }); setChannels((c) => [...c, data]); setNewCh(""); toast.success("Channel created"); } catch (e) { toast.error(errText(e)); } };
  const pin = async (id) => { try { await api.post(`/crews/messages/${id}/pin`); } catch (e) { toast.error(errText(e)); } };
  const pinned = messages.filter((m) => m.pinned);

  return (
    <div className="grid grid-cols-4 gap-3 h-[520px]">
      <div className="surface rounded-2xl p-2 overflow-y-auto">
        {channels.map((c) => <button key={c.id} onClick={() => setActive(c)} className={`w-full flex items-center gap-1.5 text-sm px-2.5 py-2 rounded-lg mb-1 ${active?.id === c.id ? "bg-white/10 text-gold" : "text-muted-foreground hover:bg-white/5"}`} data-testid={`crew-channel-${c.name}`}><Hash size={14} />{c.name}</button>)}
        {isManager && <div className="flex gap-1 mt-2"><input value={newCh} onChange={(e) => setNewCh(e.target.value)} placeholder="new-channel" className="surface-2 border border-white/10 rounded-md text-xs h-8 px-2 w-full min-w-0" data-testid="crew-newchannel-input" /><button onClick={createCh} className="btn-blue rounded-md w-8 h-8 flex items-center justify-center shrink-0" data-testid="crew-newchannel-btn"><Plus size={14} /></button></div>}
      </div>
      <div className="col-span-3 surface rounded-2xl flex flex-col">
        {pinned.length > 0 && <div className="border-b border-white/8 p-2 text-xs text-gold flex items-center gap-1"><Pin size={12} />{pinned[pinned.length - 1].text}</div>}
        <div className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="crew-chat-messages">
          {messages.map((msg) => (
            <div key={msg.id} className="flex gap-2.5 group">
              <Avatar src={msg.sender?.avatar_url} name={msg.sender?.display_name} size={30} />
              <div className="min-w-0 flex-1"><div className="flex items-center gap-2"><span className="text-sm font-medium">{msg.sender?.display_name}</span><span className="text-[10px] text-muted-foreground">{new Date(msg.created_at).toLocaleTimeString()}</span>{msg.pinned && <Pin size={11} className="text-gold" />}</div>{msg.text && <p className="text-sm whitespace-pre-wrap break-words">{msg.text}</p>}{msg.attachment_url && (msg.attachment_type === "video" ? <video src={mediaUrl(msg.attachment_url)} className="mt-1 max-h-60 rounded-lg" controls /> : <img src={mediaUrl(msg.attachment_url)} className="mt-1 max-h-60 rounded-lg" alt="" />)}</div>
              {isManager && <button onClick={() => pin(msg.id)} className="opacity-0 group-hover:opacity-100 text-muted-foreground"><Pin size={13} /></button>}
            </div>
          ))}
          <div ref={endRef} />
        </div>
        <div className="border-t border-white/8 p-2">
          {attach && (
            <div className="relative inline-block mb-2">
              {attachType === "video" ? <video src={mediaUrl(attach)} className="max-h-28 rounded-lg" /> : <img src={mediaUrl(attach)} className="max-h-28 rounded-lg" alt="" />}
              <button onClick={() => setAttach(null)} className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-black/80 border border-white/20 flex items-center justify-center" data-testid="crew-chat-attach-remove"><X size={12} /></button>
            </div>
          )}
          <div className="flex gap-2 items-center">
            <input ref={chatFileRef} type="file" accept="image/*,image/gif,video/*" className="hidden" onChange={pickChatFile} data-testid="crew-chat-file" />
            <button onClick={() => chatFileRef.current?.click()} disabled={uploading} className="text-muted-foreground hover:text-eblue shrink-0" title="Photo / GIF / Video" data-testid="crew-chat-attach-btn">{uploading ? <Loader2 size={18} className="animate-spin text-eblue" /> : <ImageIcon size={18} />}</button>
            <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={`Message #${active?.name || "general"}`} className="surface-2 border border-white/10 rounded-full px-4 h-10 flex-1 text-sm outline-none" data-testid="crew-chat-input" />
            <button onClick={send} className="btn-blue rounded-full w-10 h-10 flex items-center justify-center shrink-0" data-testid="crew-chat-send"><Send size={16} /></button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MediaTab({ crew }) {
  const [media, setMedia] = useState(null);
  useEffect(() => { api.get(`/crews/${crew.id}/media`).then(({ data }) => setMedia(data)); }, [crew.id]);
  if (!media) return <Loading />;
  if (media.length === 0) return <Empty icon={ImageIcon} title="No media yet" hint="Media shared in the crew feed appears here." />;
  return <div className="grid grid-cols-3 md:grid-cols-4 gap-2">{media.map((m) => <img key={m.id} src={mediaUrl(m.media_url)} className="w-full aspect-square object-cover rounded-xl" alt="" />)}</div>;
}

function CollabsTab({ crew, isMember }) {
  const [collabs, setCollabs] = useState(null);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", category: "Music", project: "", date: "", description: "" });
  const load = () => api.get(`/crews/${crew.id}/collabs`).then(({ data }) => setCollabs(data));
  useEffect(() => { load(); }, [crew.id]); // eslint-disable-line
  const create = async () => { if (!f.title.trim()) return; try { await api.post(`/crews/${crew.id}/collabs`, f); setOpen(false); setF({ title: "", category: "Music", project: "", date: "", description: "" }); load(); toast.success("Posted"); } catch (e) { toast.error(errText(e)); } };
  const interest = async (id) => { try { await api.post(`/crews/collabs/${id}/interest`); load(); } catch (e) { toast.error(errText(e)); } };
  const status = ["Music", "Film", "Photography", "Fashion", "Events", "Business", "Gaming", "Streaming", "Marketing", "Other"];
  return (
    <div className="max-w-2xl space-y-4">
      {isMember && (open
        ? <div className="surface rounded-2xl p-4 space-y-2">
            <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Looking for… (e.g. Videographer)" className="surface-2 border-white/10" data-testid="collab-title" />
            <div className="grid grid-cols-2 gap-2">
              <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="surface-2 border border-white/10 rounded-md h-10 px-2 text-sm">{status.map((s) => <option key={s}>{s}</option>)}</select>
              <Input value={f.project} onChange={(e) => setF({ ...f, project: e.target.value })} placeholder="Project (e.g. Music Video)" className="surface-2 border-white/10" />
            </div>
            <Input value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} placeholder="When? (e.g. Saturday)" className="surface-2 border-white/10" />
            <Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Describe the opportunity" className="surface-2 border-white/10" />
            <div className="flex gap-2 justify-end"><Button onClick={() => setOpen(false)} variant="outline" className="rounded-full border-white/15 bg-white/5">Cancel</Button><Button onClick={create} className="btn-gold rounded-full" data-testid="collab-submit">Post Opportunity</Button></div>
          </div>
        : <Button onClick={() => setOpen(true)} className="btn-gold rounded-full" data-testid="collab-new-btn"><Plus size={15} className="mr-1" />Post Opportunity</Button>)}
      {!collabs ? <Loading /> : collabs.length === 0 ? <Empty icon={Handshake} title="No opportunities yet" /> : collabs.map((c) => (
        <div key={c.id} className="surface rounded-2xl p-4" data-testid={`collab-${c.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div><div className="text-xs text-gold uppercase tracking-wide">{c.category}{c.project ? ` · ${c.project}` : ""}</div><div className="font-semibold mt-0.5">{c.title}</div></div>
            <span className="text-[10px] uppercase px-2 py-1 rounded-full surface-2 capitalize">{c.status.replace("_", " ")}</span>
          </div>
          {c.date && <div className="text-xs text-muted-foreground mt-1">📅 {c.date}</div>}
          {c.description && <p className="text-sm mt-2">{c.description}</p>}
          <div className="flex items-center gap-2 mt-3">
            <Button onClick={() => interest(c.id)} size="sm" className="btn-blue rounded-full" data-testid={`collab-interest-${c.id}`}>I'm Interested</Button>
            <div className="flex -space-x-2">{(c.interested_users || []).slice(0, 5).map((u) => <Avatar key={u.id} src={u.avatar_url} name={u.display_name} size={24} className="ring-2 ring-[#030303]" />)}</div>
            {c.interested?.length > 0 && <span className="text-xs text-muted-foreground">{c.interested.length} interested</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

function EventsTab({ crew, isMember, canCreate }) {
  const [events, setEvents] = useState(null);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", description: "", start_at: "", location: "" });
  const load = () => api.get(`/crews/${crew.id}/events`).then(({ data }) => setEvents(data));
  useEffect(() => { load(); }, [crew.id]); // eslint-disable-line
  const create = async () => { if (!f.title.trim() || !f.start_at) return toast.error("Title and date required"); try { await api.post(`/crews/${crew.id}/events`, f); setOpen(false); setF({ title: "", description: "", start_at: "", location: "" }); load(); toast.success("Event created"); } catch (e) { toast.error(errText(e)); } };
  const rsvp = async (id) => { try { await api.post(`/crews/events/${id}/rsvp`); load(); toast.success("You're going!"); } catch (e) { toast.error(errText(e)); } };
  return (
    <div className="max-w-2xl space-y-4">
      {canCreate && (open
        ? <div className="surface rounded-2xl p-4 space-y-2">
            <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Event title" className="surface-2 border-white/10" data-testid="event-title" />
            <Input type="datetime-local" value={f.start_at} onChange={(e) => setF({ ...f, start_at: e.target.value })} className="surface-2 border-white/10" data-testid="event-date" />
            <Input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder="Location / world" className="surface-2 border-white/10" />
            <Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Details" className="surface-2 border-white/10" />
            <div className="flex gap-2 justify-end"><Button onClick={() => setOpen(false)} variant="outline" className="rounded-full border-white/15 bg-white/5">Cancel</Button><Button onClick={create} className="btn-gold rounded-full" data-testid="event-submit">Create Event</Button></div>
          </div>
        : <Button onClick={() => setOpen(true)} className="btn-gold rounded-full" data-testid="event-new-btn"><Plus size={15} className="mr-1" />New Event</Button>)}
      {!events ? <Loading /> : events.length === 0 ? <Empty icon={Calendar} title="No events scheduled" /> : events.map((e) => (
        <div key={e.id} className="surface rounded-2xl p-4 flex items-center gap-4" data-testid={`event-${e.id}`}>
          <div className="text-center shrink-0"><div className="font-display font-bold text-xl text-gold">{new Date(e.start_at).getDate() || "—"}</div><div className="text-[10px] uppercase text-muted-foreground">{new Date(e.start_at).toLocaleString("en", { month: "short" })}</div></div>
          <div className="flex-1 min-w-0"><div className="font-semibold">{e.title}</div><div className="text-xs text-muted-foreground">{e.location} · {new Date(e.start_at).toLocaleString()}</div>{e.description && <p className="text-sm mt-1">{e.description}</p>}</div>
          {isMember && <Button onClick={() => rsvp(e.id)} size="sm" className="btn-blue rounded-full shrink-0" data-testid={`event-rsvp-${e.id}`}>{(e.attendees || []).length} Going</Button>}
        </div>
      ))}
    </div>
  );
}

function RankingsTab({ crew }) {
  const [charts, setCharts] = useState(null);
  const [season, setSeason] = useState(null);
  useEffect(() => {
    api.get("/crews/charts?category=Top%20Crews").then(({ data }) => setCharts(data));
    api.get("/crews/seasons/current").then(({ data }) => setSeason(data)).catch(() => {});
  }, []);
  if (!charts) return <Loading />;
  const mySeason = season?.leaderboard?.find((c) => c.id === crew.id);
  return (
    <div className="max-w-2xl space-y-6">
      {season && (
        <div className="surface rounded-2xl p-4" data-testid="crew-season">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2 font-semibold"><Award size={16} className="text-gold" />Crew of the Month</div>
            <span className="text-xs text-muted-foreground">{season.label}</span>
          </div>
          <p className="text-xs text-muted-foreground mb-3">Ranked by Crew Score gained this month. The #1 crew earns a permanent trophy.</p>
          {mySeason && <div className="text-xs text-eblue mb-2" data-testid="crew-season-rank">This crew: #{mySeason.season_rank} · +{formatNum(mySeason.season_score)} this month</div>}
          <div className="space-y-1.5">
            {(season.leaderboard || []).slice(0, 5).map((c) => (
              <div key={c.id} className={`flex items-center gap-3 rounded-lg p-2 ${c.id === crew.id ? "glass-gold" : "surface-2"}`} data-testid={`season-row-${c.handle}`}>
                <div className="font-display font-bold w-6 text-center text-gold">#{c.season_rank}</div>
                <CrewEmblem crew={c} size={30} />
                <div className="flex-1 min-w-0 text-sm font-medium truncate">{c.name}</div>
                <div className="text-sm text-gold font-semibold">+{formatNum(c.season_score)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div>
        <div className="text-sm font-semibold mb-2 flex items-center gap-2"><Trophy size={15} className="text-gold" />All-Time Top Crews</div>
        <div className="space-y-2">
          {charts.entries.map((c) => (
            <div key={c.id} className={`flex items-center gap-3 rounded-xl p-3 ${c.id === crew.id ? "surface glass-gold" : "surface"}`} data-testid={`ranking-${c.handle}`}>
              <div className="font-display font-bold text-lg w-8 text-center text-gold">#{c.rank}</div>
              <CrewEmblem crew={c} size={36} />
              <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{c.name}</div><div className="text-xs text-muted-foreground">@{c.handle}</div></div>
              <div className="text-sm text-gold font-semibold">{formatNum(c.score)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AboutTab({ crew }) {
  return (
    <div className="surface rounded-2xl p-5 max-w-xl space-y-3 text-sm">
      <div><div className="text-xs text-muted-foreground">Description</div><p>{crew.description || "No description."}</p></div>
      <div><div className="text-xs text-muted-foreground">Categories</div><p>{(crew.categories || []).join(", ")}</p></div>
      <div><div className="text-xs text-muted-foreground">Primary world</div><p>{crew.primary_world}</p></div>
      <div><div className="text-xs text-muted-foreground">Founder</div><p>{crew.founder?.display_name} (@{crew.founder?.username})</p></div>
      <div><div className="text-xs text-muted-foreground">Founded</div><p>{new Date(crew.created_at).toLocaleDateString()}</p></div>
      <div><div className="text-xs text-muted-foreground">Members</div><p>{crew.member_count} / {crew.member_limit}</p></div>
    </div>
  );
}

function ShareCrewModal({ crew, onClose }) {
  const url = crewUrl(crew.handle);
  const [storyOpen, setStoryOpen] = useState(false);
  const cardUrl = `${process.env.REACT_APP_BACKEND_URL}/api/og/story/crew/${crew.id}.png`;
  const copy = () => { navigator.clipboard?.writeText(url); toast.success("Crew link copied"); };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="crew-share-modal">
      <div className="surface rounded-2xl p-6 w-full max-w-sm relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-muted-foreground"><X size={18} /></button>
        <div className="flex items-center gap-2 mb-4"><CrewEmblem crew={crew} size={40} /><div><div className="font-display font-bold">{crew.name}</div><div className="text-xs text-muted-foreground">@{crew.handle}</div></div></div>
        <button onClick={() => setStoryOpen(true)} className="w-full btn-gold rounded-xl py-3 flex items-center justify-center gap-2 mb-3 font-semibold text-sm" data-testid="crew-story-btn"><Instagram size={16} />Share to Instagram Story</button>
        <div className="flex items-center gap-2 surface-2 rounded-lg px-3 h-10"><span className="text-xs font-mono truncate flex-1">{url}</span><button onClick={copy} className="text-eblue" data-testid="crew-share-copy"><Copy size={15} /></button></div>
      </div>
      {storyOpen && <StoryShareModal username={crew.handle} imageUrl={cardUrl} link={url} filename={`crew-${crew.handle}`} onClose={() => setStoryOpen(false)} />}
    </div>
  );
}
